import { useState, useEffect } from 'react';
import { useGetConversationById } from './use-conversation';
import { useSearchParams } from 'react-router-dom';

interface UseGroupShareModalReturn {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  shareToken: string;
  setShareToken: (value: string) => void;
  handleOpenModal: () => void;
  isLoading: boolean;
}

/**
 * Custom hook to manage group share modal state and logic
 * @param conversationId - The conversation ID to fetch share token for
 * @returns Modal state and handlers
 */
export const useGroupShareModal = (conversationId?: string): UseGroupShareModalReturn => {
  const [showModal, setShowModal] = useState(false);
  const [shareToken, setShareToken] = useState('');
  const [pendingOpen, setPendingOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const isIncognitoChat = searchParams.get('incognito-chat') === 'true';

  // Fetch conversation data to get share token
  const { data: conversation, isLoading } = useGetConversationById(conversationId as string, isIncognitoChat);

  // Sync share token from conversation data
  useEffect(() => {
    if (conversation?.shareMetadata?.[0]?.token) {
      setShareToken(conversation.shareMetadata[0].token);
    }
  }, [conversation?.shareMetadata]);

  // Reset token when conversationId becomes null/undefined
  useEffect(() => {
    if (!conversationId) {
      setShareToken('');
      setPendingOpen(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (pendingOpen && !isLoading) {
      setShowModal(true);
      setPendingOpen(false);
    }
  }, [pendingOpen, isLoading, shareToken]);

  // Handle opening the appropriate modal based on token existence
  const handleOpenModal = () => {
    setPendingOpen(true);
  };

  return {
    showModal,
    setShowModal,
    shareToken,
    setShareToken,
    handleOpenModal,
    isLoading,
  };
};
